import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { GlassCard } from "./GlassCard"
import { Trophy, X, Star, Crown } from "lucide-react"

interface Achievement {
  id: string
  title: string
  description: string
  icon: string
  tier: 'bronze' | 'silver' | 'gold' | 'apex'
  points: number
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
}

interface AchievementUnlockProps {
  achievement: Achievement | null
  isVisible: boolean
  onClose: () => void
}

const tierColors = {
  bronze: 'from-orange-600 to-orange-400',
  silver: 'from-gray-400 to-gray-200',
  gold: 'from-yellow-500 to-yellow-300',
  apex: 'from-primary to-accent'
}

const rarityColors = {
  common: 'border-gray-500',
  rare: 'border-blue-500',
  epic: 'border-purple-500', 
  legendary: 'border-yellow-500'
}

export function AchievementUnlock({ 
  achievement, 
  isVisible, 
  onClose 
}: AchievementUnlockProps) {
  const [stage, setStage] = useState<'entering' | 'showing' | 'celebrating'>('entering')

  useEffect(() => {
    if (isVisible && achievement) {
      setStage('entering')
      
      const timer1 = setTimeout(() => setStage('showing'), 500)
      const timer2 = setTimeout(() => setStage('celebrating'), 1500)
      const timer3 = setTimeout(() => onClose(), 4000)
      
      return () => {
        clearTimeout(timer1)
        clearTimeout(timer2) 
        clearTimeout(timer3)
      }
    }
  }, [isVisible, achievement, onClose])

  if (!isVisible || !achievement) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {stage === 'celebrating' && (
          <>
            {/* Floating Stars */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-ping"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${1 + Math.random()}s`
                }}
              >
                <Star size={8} className="text-primary" />
              </div>
            ))}
          </>
        )}
      </div>

      {/* Achievement Card */}
      <div
        className={`
          relative transform transition-all duration-500
          ${stage === 'entering' ? 'scale-0 rotate-180' : 'scale-100 rotate-0'}
          ${stage === 'celebrating' ? 'animate-pulse' : ''}
        `}
      >
        <GlassCard className="max-w-md mx-4" blur="lg">
          {/* Close Button */}
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 z-10"
            onClick={onClose}
          >
            <X size={16} />
          </Button>

          <CardContent className="p-8 text-center space-y-6">
            {/* Header */}
            <div className="space-y-2">
              <Badge className="bg-accent text-accent-foreground">
                Achievement Unlocked!
              </Badge>
              <h2 className="text-2xl font-bold text-foreground">
                Congratulations!
              </h2>
            </div>

            {/* Achievement Icon */}
            <div className="relative">
              <div 
                className={`
                  w-24 h-24 mx-auto rounded-full border-4 
                  ${rarityColors[achievement.rarity]}
                  bg-gradient-to-br ${tierColors[achievement.tier]}
                  flex items-center justify-center text-4xl
                  transform transition-all duration-1000
                  ${stage === 'showing' ? 'scale-110' : 'scale-100'}
                  ${stage === 'celebrating' ? 'animate-bounce' : ''}
                `}
              >
                {achievement.tier === 'apex' ? (
                  <Crown className="text-black" size={32} />
                ) : (
                  achievement.icon
                )}
              </div>

              {/* Glow Effect */}
              {stage === 'celebrating' && (
                <div className="absolute inset-0 -m-4 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-lg animate-pulse" />
              )}
            </div>

            {/* Achievement Details */}
            <div className="space-y-3">
              <h3 className="text-xl font-bold text-foreground">
                {achievement.title}
              </h3>
              
              <p className="text-muted-foreground leading-relaxed">
                {achievement.description}
              </p>

              {/* Tier and Points */}
              <div className="flex items-center justify-center gap-4">
                <Badge 
                  className={`
                    capitalize bg-gradient-to-r ${tierColors[achievement.tier]}
                    text-black border-0
                  `}
                >
                  {achievement.tier} Tier
                </Badge>
                
                <div className="flex items-center gap-1 text-accent font-semibold">
                  <Trophy size={16} />
                  +{achievement.points} pts
                </div>
              </div>

              {/* Rarity Indicator */}
              <div className="flex items-center justify-center gap-2">
                <div className={`w-2 h-2 rounded-full bg-current ${rarityColors[achievement.rarity].replace('border-', 'text-')}`} />
                <span className="text-sm text-muted-foreground capitalize">
                  {achievement.rarity} Achievement
                </span>
              </div>
            </div>

            {/* Progress Animation */}
            {stage !== 'entering' && (
              <div className="w-full bg-muted/20 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-2000 ease-out"
                  style={{ 
                    width: stage === 'celebrating' ? '100%' : '0%',
                    transition: 'width 2s ease-out'
                  }}
                />
              </div>
            )}

            {/* Action Button */}
            <Button
              onClick={onClose}
              className="bg-primary hover:bg-primary/90 text-black font-semibold w-full"
            >
              Continue Your Journey
            </Button>
          </CardContent>
        </GlassCard>
      </div>
    </div>
  )
}

// Example usage hook
export function useAchievementUnlock() {
  const [achievement, setAchievement] = useState<Achievement | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  const showAchievement = (newAchievement: Achievement) => {
    setAchievement(newAchievement)
    setIsVisible(true)
  }

  const hideAchievement = () => {
    setIsVisible(false)
    setTimeout(() => setAchievement(null), 500)
  }

  return {
    achievement,
    isVisible,
    showAchievement,
    hideAchievement
  }
}